import './SubscriptionReports.scss';
import React, {Fragment, useEffect, useState, useMemo, useRef, useCallback} from 'react';
import {useTenantApi} from '@/hooks/useTenantApi';
import ReportInformation from '@/views/ReportInformation';
import {alert as byzzerAlert, ByzzerLink, ByzzerModal, openModal, useModals} from '@/components/form';
import {orderedSectionHeaders, reportDescriptionUrl} from '@/config/globalVars';
import {useApp, useEvents, useUser} from '@/contexts/UserContext';
import {toRunsText, useBetterNavigate, snakeCaseToTitleCase} from '@/utils';
import {ByzzerMask} from '@/components/ByzzerMask/ByzzerMask';
import {TrackClick} from '@/analytics';
import {TipIcon} from '@/components/icons';
import {useSuiteCommerceLink} from '@/components/SuiteCommerceSSO';
import FavoriteIcon from '@/components/icons/FavoriteIcon';
import {getAllReports} from '@/services/product.service';
import {Link, useParams} from 'react-router-dom';
import classnames from 'classnames';
import {ReportCardBadge} from '@/views/MyReports/ReportCardBadge';
import StackedReportFilters from '@/views/MyReports/StackedReportFilters';
import {SubscriptionInfo} from '@/constants/subscription.constants'
import { ByzzerButton } from '@byzzer/ui-components';
import { ByzzerCard } from "@/components/ByzzerCard";
import useReports from '@/hooks/useReports';
import useCanRunReport from '@/hooks/useCanRunReport';
import { NavigationAnalyticsType } from '@/types/AnalyticsTypes';

const FREE_GROUP_TITLE = 'Included in Free Subscription';
const baseClassName = 'subscription-reports';

type SubscriptionReportProps = {
    changeReport?: boolean;
    handleChangeReport?: (reportType: string, sku: string) => void;
}

function SubscriptionReport( {
    changeReport = false,
    handleChangeReport
}: SubscriptionReportProps) {

    const {createUserFavorite, deleteUserFavorite, debouncedGetMySubscriptionUsage, getUserFavorite,} = useTenantApi();
    const events = useEvents();
    const {user, subscription, reloadSubscription, subscribedSkus, admins} = useUser();
    const {allProducts, reportFilterOptions} = useApp();
    const scaLink = useSuiteCommerceLink();
    const [isFree, setIsFree] = useState(subscription?.metadata?.isFree === true);
    const allReports  = useReports({ onlySubscribed: false });
    const userReports = useReports({ onlySubscribed: true });
    const [reports, setReports] = useState<typeof allReports>([]);
    // const [subscriptionReports, setSubscriptionReports] = useState([]);
    // maybe this isn't needed
    const [favoriteSkus, setFavoriteSkus] = useState<string[]>([]);
    const [remainingCoreRuns, setRemainingCoreRuns] = useState<number>();
    const [remainingSmartRuns, setRemainingSmartRuns] = useState<number>();

    const [reportInformationView, setReportInformationView] = useState(false);
    const [selectedReport, setSelectedReport] = useState<(typeof allReports)[number] | undefined>();
    const navigate = useBetterNavigate();
    const [loading, setLoading] = useState(false);
    const [sectionHeaders, setSectionHeaders] = useState<string[]>([]);
    const [filterType, setFilterType] = useState(0);
    const [showAllReports, setShowAllReports] = useState(isFree);
    // const [filteredReports, setFilteredReports] = useState<typeof reports>([]);
    const [skuFilter, setSkuFilter] = useState<string[]>([]);
    const [canRunReports, setCanRunReports] = useState(false);
    const [showReportFilters, setShowReportFilters] = useState(true);
    const {openErrorModal} = useModals();
    const {id} = useParams();
    const abortControllerRef = useRef<AbortController>(new AbortController());

    const validateReportCountAndShow = useCanRunReport({
        onChangeReport: handleChangeReport,
        changeReport,
        baseClassName
    })

    const wrapperClass = classnames('subscription-reports', {
        'ad-hoc-reports--readonly': !canRunReports,
        'ad-hoc-reports--runnable': canRunReports,
    });


    useEffect(() => {
        setLoading(true);
        loadSubscription();
        getFavorites();
        return () => {
            abortControllerRef.current?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (id && userReports?.length) {
            const filteredReport = userReports.find(({sku}) => id === sku);
            if (filteredReport) {
                setReportInformationView(true);
                setSelectedReport(filteredReport);
            } else {
                navigate('/my-reports/subscription', { replace: true });
            }
        }
    }, [id, userReports, navigate]);

    useEffect(() => {
        if (subscription) {
            const freeStatus = subscription?.metadata?.isFree === true;
            setIsFree(freeStatus);
            if (freeStatus && !showAllReports) {
                setShowAllReports(true);
            }
        }
    }, [subscription, showAllReports]);

    useEffect(() => {
        if (user?.role) {
            setCanRunReports(['user', 'admin'].includes(user?.role));
        }
    }, [user]);

    const visibleReports = useMemo(() => {
        if (!allReports?.length && !userReports?.length) {
            return [];
        }
        let baseReportList = showAllReports ? allReports : userReports;
        if (skuFilter?.length) {
            return baseReportList.filter(({sku}) => skuFilter.includes(sku));
        } else {
            return baseReportList;
        }
    }, [allReports, userReports, showAllReports, skuFilter]);

    const orderedSectionHeadersFromConfig = useMemo(() => {
        const headers = reportFilterOptions
            ?.find(({ code }) => code === 'report_section')?.values
            ?.toSorted((a, b) => Number(a?.displayOrder) - Number(b?.displayOrder))
            ?.flatMap((productFilterOptionValue) => productFilterOptionValue.display?.trim())
            ?.filter((display): display is string => (
                display !== FREE_GROUP_TITLE &&
                display !== undefined
            ));
        return headers ?? [];
    }, [reportFilterOptions]);

    useEffect(() => {
        if (isFree) {
            const hasVisibleFreeReports = visibleReports.some(report => subscribedSkus.includes(report.sku));
            const headers = hasVisibleFreeReports ? [FREE_GROUP_TITLE, ...orderedSectionHeadersFromConfig] : [...orderedSectionHeadersFromConfig];
            setSectionHeaders(headers);
        } else {
            setSectionHeaders(orderedSectionHeadersFromConfig);
        }
    }, [visibleReports, orderedSectionHeadersFromConfig, isFree, subscribedSkus]);

    useEffect(() => {
        const [event] = events;
        if (['contract_items_changed', 'subscription_report_run'].includes(event?.type)) {
            reloadSubscription();
            loadSubscription(false);
        }
    }, [events, reloadSubscription]);

    async function loadSubscription(showLoadingIndicator = true) {
        try {
            // if (showLoadingIndicator) setLoading(true);

            const [{basicReports, premiumReports}] = await Promise.all([debouncedGetMySubscriptionUsage()]);

            setRemainingCoreRuns(basicReports ? basicReports.limit - basicReports.used : 0);
            setRemainingSmartRuns(premiumReports ? premiumReports.limit - premiumReports.used : 0);
        } catch (err: any) {
            if (err?.code === 'subscription_not_found') {
                return openErrorModal({
                    title: `Subscription Not Found`,
                    content: (<>
                            <p>
                            Currently you don't have any subscription. Please reach out to your Byzzer sales representative to purchase.
                            </p>
                    </>),
                    errorId: err?.id
                });
            } else {
                return openErrorModal({
                    title: `Something Unexpected Happened`,
                    content: (<>
                            <p>Fear not our engineering team is on the job.</p>
                    </>),
                    errorId: err?.id
                });
            }
        } finally {
            if (showLoadingIndicator) {
                setLoading(false);
            }
        }
    }

    async function getFavorites() {
        let skus = await getUserFavorite('subscription', abortControllerRef.current?.signal);
        setFavoriteSkus(skus);
    }

    function handleReportClick(report: (typeof allReports)[number]) {
        if (!report?.reportType) return;

        if (subscribedSkus.includes(report.sku)) {
            validateReportCountAndShow(report);
        } else {
            byzzerAlert({
                // @ts-expect-error: alert is older and doesnt have types
                onResolve: () => {
                },
                content: (
                    <p>Please reach out to your Byzzer sales representative</p>
                ),
            });
        }
    }

    async function onFavoriteClick(event, productSku) {
        event.preventDefault();
        event.stopPropagation();
        /** Delete If Available */
        if (favoriteSkus.includes(productSku)) {
            favoriteSkus.splice(favoriteSkus.indexOf(productSku), 1);
            // expanding the array before setting is necessary to change the reference and force react to update
            setFavoriteSkus([...favoriteSkus]);
            // we only need to this check when removing a favorite because it wouldn't be visible to toggle otherwise
            if (filterType === 4) {
                setSkuFilter([...favoriteSkus]);
            }
            await deleteUserFavorite('subscription', productSku);
        } else {
            /** Add If not available */
            favoriteSkus.push(productSku);
            setFavoriteSkus([...favoriteSkus]);
            await createUserFavorite('subscription', productSku);
        }
    }

    const isReportInGroup = useCallback((report: (typeof allReports)[number], group: string): boolean => {
            const { sku, metadata } = report;
            const reportSectionHeader = metadata?.reportOptions?.sectionHeader;
            const isInFreeGroup = isFree && subscribedSkus.includes(sku);

            if (group === FREE_GROUP_TITLE) {
                return isInFreeGroup;
            } else {
                return !isInFreeGroup && group === reportSectionHeader;
            }
    }, [isFree, subscribedSkus]);

    const groupedVisibleReports = useMemo(() => {
        const groups: Record<string, typeof allReports> = {};

        sectionHeaders.forEach(header => {
            groups[header] = [];
        });

        visibleReports.forEach(report => {
            const groupName = sectionHeaders.find(header => isReportInGroup(report, header));
            if (groupName && groups[groupName]) {
                groups[groupName].push(report);
            }
        });

        return groups;
    }, [visibleReports, sectionHeaders, isReportInGroup]);


    const allReportsBtnType = !showAllReports ? 'negative' : '';
    const subscribedReportsBtnType = showAllReports ? 'negative' : '';

    const showCalculatingText = (loading || remainingCoreRuns === undefined || remainingSmartRuns === undefined);

    function handleTipIconClick(report: typeof allReports[number]) {
        setReportInformationView(!reportInformationView);
        setSelectedReport(report);
    }

    async function validateReport(report: typeof allReports[number]) {
        if (!report?.reportType) return false;

        if (subscribedSkus.includes(report.sku)) {
            const canRunReport = await validateReportCountAndShow(report, {} as NavigationAnalyticsType, true);
            return canRunReport ?? false;
        }

        return false;
    }

    return (
        <div className={wrapperClass}>
            <ByzzerMask show={loading} loading={loading}/>
            <div className={`${baseClassName}__header-wrapper`}>
                <div className={`${baseClassName}__usage`}>
                    {showCalculatingText ? <span>Calculating remaining report runs...</span> : (
                        (!(subscription?.active) ? (
                                <p>
                                    {SubscriptionInfo.SUBSCRITPION_EXPIRED}
                                </p>
                    ) : (
                        <p>
                            You have{' '}
                            <span>
                                        {toRunsText(remainingCoreRuns)} core report
                                        run{remainingCoreRuns === 1 ? '' : 's'} 
                                    </span>
                                        {' '}and{' '}
                            <span>
                                        {toRunsText(remainingSmartRuns)} smart report
                                        run{remainingSmartRuns === 1 ? '' : 's'}{' '}
                                left{' '}
                            </span>{' '}
                            in your subscription.
                            {remainingCoreRuns === 0 &&
                                ' Reach out to your sales associate or contact us on chat to purchase more runs.'}
                        </p>
                            )
                    ))}
                </div>
                {/* <ToggleGroup options={reportTypes} value={showAllReports} onChange={setShowAllReports}  ></ToggleGroup> */}
                <div className={'inclusion-toggle'}>
                    <div className={'inclusion-toggle__control'}>
                        <ByzzerButton
                            className={classnames('inclusion-toggle__control-label')}
                            onClick={() => setShowAllReports(true)}
                            type={allReportsBtnType}
                        >
                            All Reports
                        </ByzzerButton>
                        <ByzzerButton
                            className={classnames('inclusion-toggle__control-label')}
                            onClick={() => setShowAllReports(false)}
                            type={subscribedReportsBtnType}
                        >
                            Subscribed Reports
                        </ByzzerButton>
                    </div>
                </div>
            </div>
            <div className={`${baseClassName}__content`}>
                <div className="report-groups">
                    {Object.entries(groupedVisibleReports).map(([group, sectionReports]) => {
                        if (!sectionReports.length) {
                            return <Fragment key={group}/>;
                        }

                        return (
                            <div className={'report-group'} key={group}>
                                <div className="report-group__title">{group}</div>
                                <div className="report-group__cards">
                                    {sectionReports.map((report) => {
                                        const { sku } = report;
                                        // // Todo: Create badge/badgetype in alby
                                        // const reportTypeBadge = sectionHeader?.trim()?.toLowerCase() === 'wellness reports' ? 'Wellness' : snakeCaseToTitleCase(reportType);
                                        const isLocked = !subscribedSkus.includes(sku);

                                        return (
                                            <SubscriptionReportsCard
                                                key={sku}
                                                report={report}
                                                isLocked={isLocked}
                                                handleReportClick={handleReportClick}
                                                onFavoriteClick={onFavoriteClick}
                                                isFavorite={favoriteSkus.includes(sku)}
                                                handleTipIconClick={handleTipIconClick}
                                                validateReport={validateReport}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                 { !loading && visibleReports.length === 0 && (
                        <div className="ad-hoc-reports__label">No Subscription reports found.</div>
                    )}
                </div>
                <StackedReportFilters
                    reportHistory={allReports}
                    favoriteSkus={favoriteSkus}
                    onChange={setFilterType}
                    onFilter={setSkuFilter}
                    from="subscription-reports"
                    changeReport={changeReport}
                    appliesTo={"report"}
                />
            </div>
            {selectedReport && (
                <ByzzerModal
                    show={reportInformationView}
                    onClose={() => setReportInformationView(false)}
                    heading={selectedReport?.title}
                    size={'large'}
                    headerType={'normal'}
                    extraIcon={
                        canRunReports &&
                        subscribedSkus.includes(selectedReport.sku) && (
                            <ByzzerButton label={'Run Report'} onClick={() => handleReportClick(selectedReport)}/>
                        )
                    }
                >
                    <ReportInformation
                        sku={selectedReport.sku}
                        disabled={!canRunReports || !subscribedSkus.includes(selectedReport.sku)}
                        url={
                            selectedReport?.metadata?.detailsUrl ||
                            `${reportDescriptionUrl}/${selectedReport?.sku}.html`
                        }
                        runReport={() => handleReportClick(selectedReport)}
                    />
                </ByzzerModal>
            )}
        </div>
    );
}

export default SubscriptionReport;

type ReportType = ProductWithMeta & {
    reportType: string | undefined;
    sectionHeader: string | undefined;
};

type SubscriptionReportsCardProps = {
    report: ReportType;
    isLocked: boolean;
    handleReportClick: (report: ReportType) => void;
    isFavorite: boolean;
    onFavoriteClick: (e,sku: string) => void;
    handleTipIconClick: (report: ReportType) => void;
    validateReport: (report: ReportType) => Promise<boolean>;
};

function SubscriptionReportsCard({
    report,
    isLocked,
    handleReportClick,
    isFavorite,
    onFavoriteClick,
    handleTipIconClick,
    validateReport,
}: SubscriptionReportsCardProps) {
    const [canRun, setCanRun] = useState(true);

    async function validate() {
        const isValid = await validateReport(report);
        setCanRun(isValid);
    }
    useEffect(() => {
        validate();
    }, []);
    const { sku, title, description, thumbnailUrl, reportType, sectionHeader } = report;
    // Todo: Create badge/badgetype in alby
    const reportTypeBadge =
        sectionHeader?.trim()?.toLowerCase() === 'wellness reports' ? 'Wellness' : snakeCaseToTitleCase(reportType);

    const pathParam = `/dashboard/configure_report`;
    const search = {
        runType: 'subscription',
        sku: sku,
    };
    const stateParam = {};

    const handleContextMenu = (event) => {
        event.preventDefault();
    };

    const content = (
        <ByzzerCard
            onClick={!canRun || isLocked ? () => handleReportClick(report) : undefined}
            shouldTrackClick={true}
            trackClickName={`My Reports Report (${report.title}) clicked`}
            title={title}
            description={description}
            thumbnailUrl={thumbnailUrl}
            thumbnailImageSize={'lg'}
            isLocked={isLocked}
            actions={
                <>
                    {' '}
                    {/* @ts-expect-error: FavoriteIcon is not typed so it thinks all props are required when only a few will do */}
                    <FavoriteIcon
                        trackClick={`Favorited Report clicked`}
                        selected={isFavorite}
                        onClick={(e) => {
                            onFavoriteClick(e, sku);
                        }}
                        onContextMenu={handleContextMenu}
                    />
                    {/* @ts-expect-error: TipIcon is not typed so it thinks all props are required when only a few will do */}
                    <TipIcon trackClick={`Report Info (${title}) clicked`} onClick={() => handleTipIconClick(report)} onContextMenu={handleContextMenu} />
                </>
            }
            badges={[reportTypeBadge]}
        />
    );
    const searchParam = new URLSearchParams(search).toString();

    return (canRun ? (
        <Link to={{ pathname: pathParam, search: searchParam ?? '' }} state={stateParam} className={`report-group__card-container`}>
            {content}
        </Link>
    ) : (
        <>{content}</>
    ))
}